// src/components/magdrops/ProjectDetails.tsx
import {
  FileText,
  Globe,
  MessageCircle,
  Package,
  Target,
  Users,
} from "lucide-react";
import { useState } from "react";

interface Package {
  id: string;
  title: string;
  subtitle: string;
  price: string;
  timeline: string;
  description: string;
  features: string[];
  perfect_for: string;
  badge?: string;
}

interface ProjectDetails {
  businessType: string;
  audience: string;
  pages: string;
  timeline: string;
  printRun: string;
  distribution: string;
}

interface ProjectDetailsProps {
  selectedPackage: Package;
  onProjectDetailsSubmit: (details: ProjectDetails) => void;
  onBack: () => void;
}

export default function ProjectDetails({
  selectedPackage,
  onProjectDetailsSubmit,
  onBack,
}: ProjectDetailsProps) {
  const [projectDetails, setProjectDetails] = useState<ProjectDetails>({
    businessType: "",
    audience: "",
    pages: "",
    timeline: "",
    printRun: "",
    distribution: "",
  });

  const businessTypes = [
    { id: "property", label: "Property Development", icon: Package },
    { id: "hospitality", label: "Hotels & Restaurants", icon: Users },
    { id: "retail", label: "Retail & High Street", icon: Globe },
    { id: "professional", label: "Professional Services", icon: Target },
    { id: "local", label: "Local Business", icon: MessageCircle },
    { id: "other", label: "Other", icon: FileText },
  ];

  const handleSubmit = () => {
    onProjectDetailsSubmit(projectDetails);
  };

  const isFormValid =
    projectDetails.businessType && projectDetails.audience.trim();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="text-center mb-8 sm:mb-16">
          <div className="inline-flex items-center bg-blue-50 text-blue-700 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
            <FileText className="w-4 h-4 mr-2" />
            <span className="text-xs sm:text-sm font-medium">
              {selectedPackage.title} • {selectedPackage.price}
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-slate-900">
            Tell us about your project
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            The more we know about your business and audience, the better we can
            tailor your magazine content and provide an accurate quote.
          </p>
        </div>

        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-sm border border-slate-200">
          <div className="space-y-6 sm:space-y-8">
            {/* Business Type Selection - Mobile Optimized Grid */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Business type *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {businessTypes.map((type) => {
                  const IconComp = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() =>
                        setProjectDetails((prev) => ({
                          ...prev,
                          businessType: type.id,
                        }))
                      }
                      className={`p-3 sm:p-4 rounded-lg border transition-all min-h-[48px] ${
                        projectDetails.businessType === type.id
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-white hover:border-slate-300 text-slate-600"
                      }`}
                    >
                      <IconComp className="w-4 h-4 mx-auto mb-1" />
                      <div className="text-xs sm:text-sm leading-tight">
                        {type.label}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Two Column Layout for Desktop, Stacked for Mobile */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Target audience *
                  </label>
                  <textarea
                    placeholder="Who are you trying to reach? (e.g. property buyers, local residents, business clients)"
                    value={projectDetails.audience}
                    onChange={(e) =>
                      setProjectDetails((prev) => ({
                        ...prev,
                        audience: e.target.value,
                      }))
                    }
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Page count
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {["8", "12", "24"].map((pages) => (
                      <button
                        key={pages}
                        onClick={() =>
                          setProjectDetails((prev) => ({ ...prev, pages }))
                        }
                        className={`py-3 rounded-lg font-medium transition-all min-h-[48px] ${
                          projectDetails.pages === pages
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        {pages} pages
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Timeline priority
                  </label>
                  <select
                    value={projectDetails.timeline}
                    onChange={(e) =>
                      setProjectDetails((prev) => ({
                        ...prev,
                        timeline: e.target.value,
                      }))
                    }
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[48px]"
                  >
                    <option value="">Select priority...</option>
                    <option value="urgent">Urgent (ASAP)</option>
                    <option value="standard">Standard timeline</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Estimated print run
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 500 copies, 1000 copies"
                    value={projectDetails.printRun}
                    onChange={(e) =>
                      setProjectDetails((prev) => ({
                        ...prev,
                        printRun: e.target.value,
                      }))
                    }
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[48px]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Distribution plan
                  </label>
                  <textarea
                    placeholder="How will you distribute? (e.g. hand delivery, events, partner locations)"
                    value={projectDetails.distribution}
                    onChange={(e) =>
                      setProjectDetails((prev) => ({
                        ...prev,
                        distribution: e.target.value,
                      }))
                    }
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 sm:pt-8">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  className={`w-full sm:w-auto px-8 py-4 rounded-lg font-semibold text-lg transition-all min-h-[48px] ${
                    isFormValid
                      ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  Get your custom quote
                </button>

                <button
                  onClick={onBack}
                  className="w-full sm:w-auto text-slate-500 hover:text-slate-700 transition-colors py-3 px-6 min-h-[48px]"
                >
                  ← Change package
                </button>
              </div>
            </div>

            {/* Form Validation Helper */}
            {!isFormValid && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-700">
                  Please complete the required fields (*) to continue
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
